import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { ClockCounterClockwise, MagnifyingGlass, Trash } from '@phosphor-icons/react';

const ACTIE_COLORS = {
  aangemaakt: '#059669', bijgewerkt: '#2563EB', verwijderd: '#DC2626',
  email_verstuurd: '#7C3AED', betaallink_aangemaakt: '#D97706',
  AUDIT_LOG_GEWIST: '#DC2626',
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState('');
  const [wiping, setWiping] = useState(false);

  const load = () => API.get('/audit-log?limit=200').then(r => setLogs(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const wipeAll = async () => {
    const pw = window.prompt(
      `⚠️ Volledige activiteitenlog wissen — ${logs.length} records.\n\n` +
      `Deze actie is ONOMKEERBAAR. De gewiste records zijn definitief verloren.\n\n` +
      `Eén traceer-entry ("AUDIT_LOG_GEWIST") blijft achter zodat de wipe-actie zelf herkenbaar is.\n\n` +
      `Bevestig met je admin-wachtwoord:`
    );
    if (!pw) return;
    setWiping(true);
    try {
      const r = await API.delete('/audit-log', { data: { password: pw } });
      alert(`✅ ${r.data.gewist} records gewist.`);
      load();
    } catch (e) {
      alert('Wissen mislukt: ' + (e.response?.data?.detail || e.message));
    } finally {
      setWiping(false);
    }
  };

  const filtered = logs.filter(l =>
    !q || l.actie?.toLowerCase().includes(q.toLowerCase()) ||
    l.entiteit?.toLowerCase().includes(q.toLowerCase()) ||
    l.entiteit_id?.toLowerCase().includes(q.toLowerCase()) ||
    l.details?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="audit-log-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="page-heading mb-0">
          <ClockCounterClockwise size={24} className="inline mr-2 text-[#002FA7]" />
          Activiteitenlog
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">{filtered.length} acties</span>
          <button
            onClick={wipeAll}
            disabled={wiping || logs.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-700 border border-red-300 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Wis de volledige activiteitenlog van deze tenant (vereist admin-wachtwoord)"
            data-testid="audit-wipe-all"
          >
            <Trash size={14} /> {wiping ? 'Bezig...' : 'Wis hele log'}
          </button>
        </div>
      </div>

      <div className="relative mb-4" style={{ maxWidth: 300 }}>
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input className="field-input" style={{ paddingLeft: 36 }} placeholder="Zoek in activiteiten..." value={q} onChange={e => setQ(e.target.value)} data-testid="audit-search" />
      </div>

      <div className="section-card">
        <div className="section-body">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Tijdstip</th>
                <th style={{ width: 140 }}>Actie</th>
                <th style={{ width: 120 }}>Entiteit</th>
                <th style={{ width: 140 }}>ID</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, i) => (
                <tr key={i}>
                  <td className="text-xs font-mono text-gray-500">
                    {l.tijdstip ? new Date(l.tijdstip).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </td>
                  <td>
                    <span className="text-xs font-semibold px-2 py-0.5 inline-block"
                      style={{ color: ACTIE_COLORS[l.actie] || '#6B7280', background: (ACTIE_COLORS[l.actie] || '#6B7280') + '15' }}>
                      {l.actie}
                    </span>
                  </td>
                  <td className="text-sm font-medium">{l.entiteit}</td>
                  <td className="text-xs font-mono text-[#002FA7]">{l.entiteit_id || '-'}</td>
                  <td className="text-xs text-gray-500 truncate max-w-xs">{l.details || '-'}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="5" className="text-center text-gray-400 py-8">Geen activiteiten gevonden</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
